<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" :data="model" scrollable
                   :title="computedTitle" @show="onShow"
                   :disable-submit-btn="!model.settlements || model.settlements.length === 0 || disableForm"
                   @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)" :show-confirmation="showConfirmation"
                   :ok-title="$t('Submit')" :is-loading="loading || submitting" modal-class="mobile-full-screen">

      <div class="row">
        <div class="col-md-3">
          <input-widget :model="model" attribute="date" type="datepicker" :disabled="disableForm"/>
        </div>
        <div class="col-md-3">
          <input-widget :model="model" attribute="method" value-field="value" display-field="${item.key}"
                        type="select2" :items="paymentMethods" :disabled="disableForm"/>
        </div>
        <div class="col-md-3" v-if="paymentBankAccounts && paymentBankAccounts.length > 0">
          <input-widget :model="model" attribute="bank_account" value-field="value"
                        display-field="${item.key}" type="select2" :items="paymentBankAccounts"
                        :disabled="disableForm"/>
        </div>
        <div class="col-md-3">
          <input-widget :model="model" attribute="status" value-field="value" display-field="${item.key}" type="select2"
                        :items="batchPaymentStatuses" :disabled="disableForm"/>
        </div>
        <div class="col-md-3">
          <currency-select-input-widget :model="model" attribute="currency_uuid"
                                        @select="onCurrencySelect" :disabled="!!model.uuid"/>
        </div>

        <div class="col-md-3">
          <input-widget :model="model" attribute="exchange_rate" :rules="exchangeRateRules" type="number"
                        @input="onExchangeRateChange"/>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <input-widget :model="model" attribute="description" type="textarea" :disabled="disableForm"/>
        </div>
      </div>

      <div class="row mt-3 mb-2">
        <div class="col-md-12 d-flex justify-content-between">
          <h5 class="mb-0">{{ $t('Settlements') }}</h5>
          <b-button :disabled="model.settlements.length > 199 || disableForm" size="sm" variant="primary"
                    @click="onAddSettlementClick()">
            <font-awesome-icon icon="plus" :disabled="disableForm"/>
            {{ $t('Add Settlement') }}
          </b-button>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <table class="info-table table table-sm lots-table">
            <tr>
              <th style="width: 15%">{{ $t('Settlement') }} <span class="text-danger">*</span></th>
              <th style="width: 15%">{{ $t('Amount') }} <span class="text-danger">*</span></th>
              <th style="width: 15%" v-if="currenciesDiffer">{{ $t('Converted Amount') }} <span
                class="text-danger">*</span></th>
              <th style="width: 15%">{{ $t('Reference Number') }}</th>
              <th style="width: 15%">{{ $t('Beneficiaries') }}</th>
              <th style="width: 15%">{{ $t('Customer') }}</th>
              <th style="width: 10%">{{ $t('Info') }}</th>
              <th style="width: 10%">{{ $t('Reconciled') }}</th>
              <th style="width: 5%">{{ $t('Actions') }}</th>
            </tr>
            <tr v-if="model.settlements.length === 0">
              <td colspan="8">
                <no-data-available height="4"/>
              </td>
            </tr>
            <template v-else>
              <template v-for="(settlementItem, idx) in model.settlements">
                <tr :key="settlementItem.uniqueKey">
                  <td>
                    <settlement-select-input-widget :model="settlementItem" attribute="settlement_uuid" :label="false"
                                                    :options="{'placeholder': $t('Settlement')}"
                                                    @select="onSettlementSelect($event, settlementItem, idx)"
                                                    @unselect="onSettlementUnselect(settlementItem)"
                                                    :disabled="disableForm || !!settlementItem.uuid"
                                                    :extra-filters="{expand: 'customer,currency,payable_amount'}"/>
                  </td>
                  <td>
                    <currency-input-widget :model="settlementItem" attribute="amount" :label="false"
                                           :placeholder="$t('Amount')" :disabled="disableForm"
                                           :currency="settlementItem.settlement ? settlementItem.settlement.currency.symbol : null"
                                           @input="onAmountChange(settlementItem)"/>
                  </td>
                  <td v-if="currenciesDiffer">
                    <currency-input-widget :model="settlementItem" attribute="converted_amount" :label="false"
                                           :disabled="disableForm"
                                           :currency="settlementCurrencySymbol"
                                           @input="onConvertedAmountChange(settlementItem)"/>
                  </td>
                  <td>
                    <input-widget :model="settlementItem" attribute="reference_number" type="text" rules="max:11"
                                  :label="false" :disabled="disableForm"/>
                  </td>
                  <td>
                    <input-widget :model="settlementItem" attribute="beneficiary_relation_uuid" value-field="uuid"
                                  type="select2"
                                  display-field="${item.receipt_no} - ${item.related_customer.display_name} - ${item.beneficiary_rate}%"
                                  :options="{'placeholder': $t('Beneficiaries')}"
                                  :items="settlementItem.beneficiaries" :disabled="disableForm" :label="false"/>
                  </td>
                  <td>
                    <div v-if="settlementItem.settlement && settlementItem.settlement.customer">
                      {{ settlementItem.settlement.customer.customer_no }} <br>
                      {{ settlementItem.settlement.customer.display_name }} <br>
                      {{ settlementItem.settlement.customer.email }}
                    </div>
                  </td>
                  <td>
                    <div v-if="settlementItem.settlement">
                      <b>{{ $t('Total') }}:</b> {{
                        settlementItem.settlement.total_amount|asCurrency(modelCurrencySymbol)
                      }}
                      <br>
                      <b>{{ $t('Payable') }}:</b> {{
                        settlementItem.settlement.payable_amount|asCurrency(modelCurrencySymbol)
                      }}
                      <br>
                      <b>{{ $t('Paid') }}:</b>
                      {{ settlementItem.settlement.paid_amount|asCurrency(modelCurrencySymbol) }}
                    </div>
                  </td>
                  <td class="text-center">
                    <input-widget :model="settlementItem" attribute="reconciled" type="checkbox" :label="false"
                                  :disabled="disableForm"/>
                  </td>
                  <td class="text-center">
                    <b-button variant="danger" size="sm" @click="onSettlementItemRemoveClick(settlementItem)"
                              :disabled="disableForm">
                      <font-awesome-icon icon="times" class="icon"/>
                    </b-button>
                  </td>
                </tr>
                <tr :key="`warning-row-${settlementItem.uniqueKey}`">
                  <td colspan="8" class="border-top-0 text-danger" v-if="settlementItem.settlement">
                    <div
                      v-if="!model.uuid && settlementItem.amount && parseFloat(settlementItem.amount) > parseFloat(settlementItem.settlement.outstanding_amount)">
                      {{ $t('The payment amount is higher than the outstanding amount of the settlement.') }}
                    </div>
                    <div v-if="settlementItem.settlement.status === 'paid' ">
                      {{ $t('Following settlement is fully paid.') }}
                    </div>
                  </td>
                </tr>
              </template>


            </template>
          </table>
        </div>
      </div>

      <div class="row mb-2">
        <div class="col-md-3 offset-9">
          <table class="w-100 text-right">
            <tr>
              <td class="w-75"><b>{{ $t('Total Payment Amount') }}:</b></td>
              <td class="w-25">{{ totalAmount|asCurrency(modelCurrencySymbol) }}</td>
            </tr>
            <tr>
              <td class="w-75"><b>{{ $t('Number of Entries') }}:</b></td>
              <td class="w-25">{{ model.settlements.length }}</td>
            </tr>
          </table>
        </div>
      </div>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>

  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import NoDataAvailable from "@/core/components/common/NoDataAvailable";
  import CurrencyInputWidget from "@/core/widgets/CurrencyInputWidget";
  import InputWidget from "@/core/components/input-widget/InputWidget";
  import {
    SOURCE_SETTLEMENT_PAYMENTS_TABLE,
    SOURCE_SETTLEMENTS_TABLE
  } from "@/modules/constants";
  import cloneDeep from "lodash/cloneDeep";
  import SettlementSelectInputWidget from "@/core/widgets/SettlementSelectInputWidget";
  import SettlementBatchPaymentModel from "@/modules/settlements/settlement-batch-payment/SettlementBatchPayment.model";
  import SettlementBatchPaymentItemModel
    from "@/modules/settlements/settlement-batch-payment/SettlementBatchPaymentItem.model";
  import InvoicesService from "@/modules/invoices/InvoicesService";
  import SettlementService from "@/modules/settlements/SettlementService";
  import CurrencySelectInputWidget from "@/core/widgets/CurrencySelectInputWidget.vue";

  const {mapState, mapActions} = createNamespacedHelpers('settlements');
  const {mapState: globalMapState} = createNamespacedHelpers('global');
  const {mapActions: settlementPaymentsActions} = createNamespacedHelpers('settlementPayments');

  export default {
    name: "SettlementBatchPaymentModal",
    components: {
      CurrencySelectInputWidget,
      SettlementSelectInputWidget,
      InputWidget,
      CurrencyInputWidget,
      NoDataAvailable,
      ArtisioModal
    },
    data() {
      return {
        showConfirmation: true,
        model: new SettlementBatchPaymentModel(),
        oldStatus: null,
        submitting: false
      }
    },
    computed: {
      ...mapState({
        modal: state => state.settlementBatchPaymentModal,
        loading: state => state.settlementBatchPaymentModal.loading,
        editObject: state => state.settlementBatchPaymentModal.object,
      }),
      ...globalMapState({
        currencies: state => state.currencies,
        paymentBankAccounts: state => state.paymentBankAccounts,
        batchPaymentStatuses: state => state.batchPaymentStatuses,
        paymentMethods: state => state.paymentMethods,
      }),
      computedTitle() {
        if (!this.model.uuid || !this.editObject) {
          return this.$i18n.t('Generate Batch Payments')
        }
        return this.$i18n.t('Update Batch Payments - {batch_id}', {batch_id: this.editObject.payment_no})
      },
      totalAmount() {
        let tmpAmount = 0
        this.model.settlements.forEach(i => tmpAmount += parseFloat(i.amount ? i.amount : 0))
        return tmpAmount
      },
      disableForm() {
        return this.model.uuid && this.oldStatus === 'approved'
      },
      currenciesDiffer() {
        if (this.model.currency_uuid && this.model.settlements.length > 0) {
          return this.model.currency_uuid !== this.model.settlements[0].settlement.currency.uuid
        }
        return this.model.currency_uuid
      },
      exchangeRateRules() {
        return this.currenciesDiffer ? 'required' : ''
      },
      modelCurrencySymbol() {
        let tmpCurrency = this.currencies.find(c => c.uuid === this.model.currency_uuid)
        return tmpCurrency ? tmpCurrency.symbol : null
      },
      settlementCurrencySymbol() {
        if (this.model.settlements.length > 0) {
          return this.model.settlements[0].settlement.currency.symbol
        }
        return null
      }
    },
    watch: {
      async editObject() {
        if (this.editObject && this.editObject.uuid) {
          let tmpObj = cloneDeep(this.editObject)
          let payments = tmpObj.payments
          delete tmpObj.payments

          this.model = new SettlementBatchPaymentModel(tmpObj)
          let objPayments = []
          let uuidsForBeneficiaries = []

          for (let payment of payments) {
            let settlementPayment = payment.settlement_payments[0]
            let tmpObj = new SettlementBatchPaymentItemModel({
              uuid: payment.uuid,
              settlement_uuid: settlementPayment.settlement.uuid,
              settlement: cloneDeep(settlementPayment.settlement),
              amount: payment.amount,
              converted_amount: settlementPayment.converted_amount,
              reference_number: payment.reference_number,
              beneficiary_relation_uuid: payment.beneficiary_relation_uuid,
              reconciled: payment.reconciled,
            })
            this.calculateOutstandingAmount(tmpObj, false)
            objPayments.push(tmpObj)
            uuidsForBeneficiaries.push(settlementPayment.settlement_uuid)
          }

          let mappedBeneficiaries = await SettlementService.getBeneficiariesMapped(uuidsForBeneficiaries)
          for (let obj of objPayments) {
            if (mappedBeneficiaries[obj.settlement_uuid] && mappedBeneficiaries[obj.settlement_uuid].length > 0) {
              obj.beneficiaries = mappedBeneficiaries[obj.settlement_uuid]
            }
          }

          this.model.settlements = objPayments
          this.oldStatus = this.model.status
        }
      }
    },
    methods: {
      ...mapActions(['hideSettlementBatchPaymentModal', 'getTableData', 'toggleSettlementBatchPaymentModalLoading']),
      ...settlementPaymentsActions({settlementPaymentsGetTableData: 'getTableData'}),

      async onShow() {
        this.showConfirmation = true;
      },
      onHidden() {
        this.model = new SettlementBatchPaymentModel();
        this.hideSettlementBatchPaymentModal();
        this.submitting = false;
      },
      async onSubmit() {
        if (this.submitting) {
          return;
        }
        this.submitting = true;
        try {
          await this.submitBatchPayment();
        } finally {
          this.submitting = false;
        }
      },
      async submitBatchPayment() {
        let currency = this.model.settlements[0].settlement.currency

        for (let item of this.model.settlements) {
          if (currency.uuid !== item.settlement.currency.uuid) {
            this.$errorDialog('You cannot create batch payment for settlements with different currencies.')
            return false
          }
        }

        this.showConfirmation = false

        this.toggleSettlementBatchPaymentModalLoading(true)

        let requestData = {
          is_settlement: true,
          uuid: this.model.uuid,
          date: this.model.date,
          method: this.model.method,
          description: this.model.description,
          bank_account: this.model.bank_account,
          status: this.model.status,
          currency_uuid: this.model.currency_uuid,
          exchange_rate: this.model.exchange_rate,
          payments: this.model.settlements.map(i => {
            return {
              uuid: i.uuid,
              settlement_uuid: i.settlement_uuid,
              amount: i.amount,
              reference_number: i.reference_number,
              reconciled: i.reconciled,
              beneficiary_relation_uuid: i.beneficiary_relation_uuid,
            }
          })
        }

        let response;
        if (!requestData.uuid) {
          response = await InvoicesService.createBatchPayment(requestData)
        } else {
          response = await InvoicesService.updateBatchPayment(requestData.uuid, requestData)
        }

        if (response.success) {
          if (!requestData.uuid) {
            this.$toast('success', this.$t('Batch payment was successfully created'));
          } else {
            this.$toast('success', this.$t('Batch payment was successfully updated'));
          }

          if (this.modal.source === SOURCE_SETTLEMENTS_TABLE) {
            await this.getTableData();
          } else if (this.modal.source === SOURCE_SETTLEMENT_PAYMENTS_TABLE) {
            await this.settlementPaymentsGetTableData()
          }
          this.toggleSettlementBatchPaymentModalLoading(false)
          this.hideSettlementBatchPaymentModal();

        } else {
          this.toggleSettlementBatchPaymentModalLoading(false)

          this.showConfirmation = true
          if (response.body.non_field_errors) {
            const listItemsArr = [];
            response.body.non_field_errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Batch Payment Error'));
          }
        }
      },
      onAddSettlementClick() {
        this.model.settlements.push(new SettlementBatchPaymentItemModel())
      },
      onSettlementItemRemoveClick(item) {
        let index = this.model.settlements.findIndex(i => i.uniqueKey === item.uniqueKey)
        if (index > -1) {
          this.model.settlements.splice(index, 1)
        }
      },
      async onSettlementSelect(data, settlementItem, idx) {
        settlementItem.settlement = data

        let showError = false
        let settlementCurrencyUuid = this.model.settlements.length > 0 ? this.model.settlements[0].settlement.currency.uuid : null

        if (!this.model.currency_uuid) {
          this.model.currency_uuid = settlementCurrencyUuid
        }

        for (let item of this.model.settlements) {
          if (item.settlement && settlementCurrencyUuid !== item.settlement.currency.uuid) {
            showError = true
          }
        }

        if (showError) {
          this.model.settlements[idx] = new SettlementBatchPaymentItemModel()
          this.$errorDialog(this.$i18n.t('You can not create batch payment for different currencies'))
          return false
        }

        this.calculateOutstandingAmount(settlementItem)
        settlementItem.beneficiaries = await SettlementService.getBeneficiariesBySettlement(settlementItem.settlement)
      },
      calculateOutstandingAmount(settlementItem, setAmount = true) {
        if (setAmount && ['cancelled', 'debited'].includes(settlementItem.settlement.status)) {
          settlementItem.amount = 0
        } else {
          settlementItem.settlement.outstanding_amount = parseFloat(settlementItem.settlement.outstanding_amount).toFixed(2)
          if (setAmount && settlementItem.settlement.outstanding_amount >= 0) {
            settlementItem.amount = parseFloat(settlementItem.settlement.outstanding_amount).toFixed(2)
            settlementItem.initial_amount = settlementItem.amount
          }
        }
      },
      onSettlementUnselect(settlementItem) {
        settlementItem.settlement = null
        settlementItem.settlement_uuid = null
        settlementItem.amount = null
      },
      onCurrencySelect() {
        this.model.exchange_rate = null
      },
      onExchangeRateChange() {
        this.$nextTick(() => {
          this.calculateAmountAll()
        })
      },
      calculateAmountAll() {
        for (let item of this.model.settlements) {
          this.calculateAmount(item)
        }
      },
      calculateAmount(item, amountSource = null) {
        if (amountSource === null) {
          amountSource = item.initial_amount
        }

        if (this.model.exchange_rate) {
          item.amount = (amountSource / this.model.exchange_rate).toFixed(2)
        }
        item.converted_amount = amountSource
      },
      onAmountChange(settlementItem) {
        if (!this.currenciesDiffer || !this.model.exchange_rate) {
          return true
        }
        settlementItem.converted_amount = (settlementItem.amount * this.model.exchange_rate).toFixed(2)
      },
      onConvertedAmountChange(settlementItem) {
        this.calculateAmount(settlementItem, settlementItem.converted_amount)
      },
    }
  }
</script>

<style lang="scss" scoped>
  @import "./../scss/variables";

  .lots-table {
    /deep/ .form-group {
      margin-bottom: 0;
    }
  }

</style>
